/**
 * SeriesChartWidget — a monthly trend for one or more COUNT metrics, from
 * /api/dashboard/v2/series.
 *
 * Config-driven: name the metric keys and the chart type. Because the series
 * comes from each metric's own registry drill spec, the line can never drift
 * from the KPI card above it, and each point is drillable back to its records.
 *
 * Click a point → the records for THAT month. The dashboard's own date filter
 * is deliberately overridden for the drill: the reader clicked March, so March
 * is the range they get, not the dashboard's twelve. A click on a multi-series
 * line chart identifies an x position rather than one line, so every measure at
 * that month is offered as its own tab instead of one being guessed at.
 *
 * Only metrics of the same KIND belong on one of these (all counts, one
 * y-scale). Mixing a count with a rate would need two scales, which the chart
 * layer refuses by design — put those in two charts.
 */
import React, { useState } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { fetchMetricSeries } from '../api/client';
import ChartWidget from './ChartWidget';
import DrilldownModal from './DrilldownModal';
import MultiDrilldownModal from './MultiDrilldownModal';

/** 'YYYY-MM' → the custom time filter covering exactly that month. */
function monthRange(period) {
  const [y, m] = String(period).split('-').map(Number);
  if (!y || !m) return null;
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const pad = (n) => String(n).padStart(2, '0');
  return {
    preset: 'custom',
    date_from: `${y}-${pad(m)}-01`,
    date_to: `${y}-${pad(m)}-${pad(last)}`,
  };
}

function monthLabel(period) {
  const [y, m] = String(period).split('-').map(Number);
  if (!y || !m) return period;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

export default function SeriesChartWidget({
  title,
  metrics = [],
  chartType = 'line',
  months = 12,
  height = 300,
  showValues = false,
  drillable = true,
}) {
  const { module, siteId, time, departmentId, dimensions, fyStartMonth, refreshMs } = useDashboard();
  const [drill, setDrill] = useState(null);

  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';
  const metricsKey = metrics.join(',');

  const { data, loading, error } = useDashboardData(
    (signal) => {
      const body = { module, metrics };
      // `months` gives a rolling window (a trend needs history regardless of the
      // selected period); without it the series follows the date filter.
      if (months) body.months = months;
      else if (time) body.time = time;
      if (siteId) body.site_id = siteId;
      if (departmentId) body.department_id = departmentId;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      if (fyStartMonth) body.fy_start_month = fyStartMonth;
      return fetchMetricSeries(body, { signal });
    },
    // A rolling `months` window is independent of the selected period, so the
    // time filter is deliberately NOT a dependency there — otherwise changing
    // the period refetches an identical series.
    [module, metricsKey, siteId, months ? '' : timeKey, departmentId, dimsKey, months, fyStartMonth],
    { enabled: !!module && metrics.length > 0, refreshMs },
  );

  const series = data?.series || [];

  const onPointClick = (point) => {
    if (!drillable) return;
    const period = point?.payload?.period ?? point?.period ?? point?.activeLabel;
    if (!period) return;
    // A click on a line chart lands on an x position, not on one line, so
    // `entries` names every visible series there. Rather than guessing which
    // line was meant, offer all of them — one tab per measure. A bar click does
    // identify its series, and a single-series chart has nothing to choose.
    const entries = Array.isArray(point?.entries) ? point.entries : null;
    const aliases = entries && entries.length > 1
      ? entries.map((e) => e.dataKey)
      : [point?.dataKey || entries?.[0]?.dataKey || series[0]?.key];
    const chosen = aliases
      .map((alias) => series.find((sd) => sd.key === alias))
      .filter(Boolean);
    if (chosen.length === 0) return;
    setDrill({ period, metrics: chosen });
  };

  return (
    <>
      <ChartWidget
        type={chartType}
        data={data?.data || []}
        xKey="period"
        series={series.map((s) => ({ key: s.key, label: s.label }))}
        title={title}
        height={height}
        showValues={showValues}
        loading={loading}
        error={error}
        onPointClick={drillable ? onPointClick : undefined}
      />
      {/* One measure → a plain records grid; several → a tab each, so the
          reader picks which line they were asking about. */}
      {drill && drill.metrics.length === 1 ? (
        <DrilldownModal
          open
          metric={drill.metrics[0].metric}
          title={`${drill.metrics[0].label} — ${monthLabel(drill.period)}`}
          subtitle="Records for the month you clicked. Click a row to open it."
          timeOverride={monthRange(drill.period)}
          onClose={() => setDrill(null)}
        />
      ) : null}
      {drill && drill.metrics.length > 1 ? (
        <MultiDrilldownModal
          open
          title={monthLabel(drill.period)}
          subtitle="Records for the month you clicked, by measure. Click a row to open it."
          tabs={drill.metrics.map((m) => ({
            id: m.key,
            label: m.label,
            metric: m.metric,
            timeOverride: monthRange(drill.period),
          }))}
          onClose={() => setDrill(null)}
        />
      ) : null}
    </>
  );
}
